<template>
    <div class="relative flex items-center gap-0.5">
        <!-- Left arrow -->
        <button
            v-if="canScrollLeft"
            type="button"
            :aria-label="localizations[lang].scrollLeft || 'Scroll left'"
            class="flex flex-shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-theme-toolbar-icon opacity-40 transition-opacity duration-150 hover:opacity-80"
            :class="arrowSizeClass"
            @click="scroll('left')"
        >
            <SvgIcon icon="chevron-left-bold" :width="arrowIconSize" :height="arrowIconSize" />
        </button>

        <!-- Scrollable container -->
        <div ref="scrollContainer" class="scrollbar-none flex-1 overflow-x-auto" @scroll="updateScrollState">
            <div class="flex flex-nowrap gap-2">
                <slot></slot>
            </div>
        </div>

        <!-- Right arrow -->
        <button
            v-if="canScrollRight"
            type="button"
            :aria-label="localizations[lang].scrollRight || 'Scroll right'"
            class="flex flex-shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-theme-toolbar-icon opacity-40 transition-opacity duration-150 hover:opacity-80"
            :class="arrowSizeClass"
            @click="scroll('right')"
        >
            <SvgIcon icon="chevron-right-bold" :width="arrowIconSize" :height="arrowIconSize" />
        </button>
    </div>
</template>

<script>
import { mapState } from 'vuex';

/**
 * A horizontal carousel component for color swatches with scroll navigation.
 * Uses slots for flexible content rendering.
 */
export default {
    name: 'ColorCarousel',

    props: {
        /**
         * Size variant for the carousel arrows.
         * 'sm' for compact filter UI, 'md' for form UI.
         */
        size: {
            type: String,
            default: 'md',
            validator: value => ['sm', 'md'].includes(value),
        },

        /**
         * Scroll step in pixels when clicking arrows.
         */
        scrollStep: {
            type: Number,
            default: 80,
        },
    },

    data() {
        return {
            canScrollLeft: false,
            canScrollRight: false,
        };
    },

    computed: {
        ...mapState(['localizations', 'lang']),

        arrowSizeClass() {
            return this.size === 'sm' ? 'h-6 w-4' : 'h-7 w-5';
        },

        arrowIconSize() {
            return this.size === 'sm' ? 10 : 12;
        },
    },

    mounted() {
        this.$nextTick(() => this.updateScrollState());
    },

    methods: {
        /**
         * Updates the visibility state of scroll arrows based on scroll position.
         */
        updateScrollState() {
            const el = this.$refs.scrollContainer;
            if (!el) return;
            this.canScrollLeft = el.scrollLeft > 0;
            this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
        },

        /**
         * Scrolls the carousel in the specified direction.
         * @param {'left' | 'right'} direction - Scroll direction
         */
        scroll(direction) {
            const el = this.$refs.scrollContainer;
            if (!el) return;
            el.scrollBy({
                left: direction === 'left' ? -this.scrollStep : this.scrollStep,
                behavior: 'smooth',
            });
        },

        /**
         * Public method to refresh scroll state.
         * Call this when content changes dynamically.
         */
        refresh() {
            this.$nextTick(() => this.updateScrollState());
        },
    },
};
</script>

<style scoped>
.scrollbar-none {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
.scrollbar-none::-webkit-scrollbar {
    display: none;
}
</style>
